import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { TaskContextLinkedAsset } from '../../models/ai-assistant.models';

export type AssetDisplayMode = 'chip' | 'table';

@Component({
  selector: 'app-asset-display',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './asset-display.component.html',
  styleUrl: './asset-display.component.css',
})
export class AssetDisplayComponent {
  @Input() assets: TaskContextLinkedAsset[] = [];
  @Input() mode: AssetDisplayMode = 'chip';
  @Input() maxRecords = 5;
  @Input() total?: number;

  get displayed(): TaskContextLinkedAsset[] {
    return this.assets.slice(0, this.maxRecords);
  }

  get overflow(): number {
    return Math.max(0, (this.total ?? this.assets.length) - this.maxRecords);
  }

  get totalCount(): number {
    return this.total ?? this.assets.length;
  }

  assetName(a: TaskContextLinkedAsset): string {
    return a.name || a.assetId || '—';
  }

  assetCategory(a: TaskContextLinkedAsset): string {
    return a.category || a.assetCategory || a.assetType || '—';
  }

  critClass(value?: string): string {
    const v = (value || '').toLowerCase();
    if (v === 'critical') return 'ad-crit--critical';
    if (v === 'high')     return 'ad-crit--high';
    if (v === 'medium')   return 'ad-crit--medium';
    if (v === 'low')      return 'ad-crit--low';
    return '';
  }

  exposureClass(value?: string): string {
    const v = (value || '').toLowerCase();
    if (v === 'public' || v === 'external') return 'ad-crit--critical';
    if (v === 'internal')                   return 'ad-crit--medium';
    if (v === 'restricted')                 return 'ad-crit--high';
    return '';
  }

  tagsLabel(a: TaskContextLinkedAsset): string {
    return (a.tags ?? []).join(', ') || '—';
  }

  resolveString(value: unknown): string {
    if (!value) return '—';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      const o = value as Record<string, unknown>;
      return String(o['name'] ?? o['email'] ?? o['displayName'] ?? o['label'] ?? '—');
    }
    return String(value);
  }

  // Tooltip content for chip mode (plain text shown in title)
  chipTooltip(a: TaskContextLinkedAsset): string {
    const lines: string[] = [];
    if (a.assetId)    lines.push(`ID: ${a.assetId}`);
    if (a.category || a.assetCategory) lines.push(`Category: ${a.category || a.assetCategory}`);
    if (a.subCategory) lines.push(`Sub-category: ${a.subCategory}`);
    if (a.criticality) lines.push(`Criticality: ${a.criticality}`);
    if (a.exposure)    lines.push(`Exposure: ${a.exposure}`);
    if (a.owner)       lines.push(`Owner: ${a.owner}`);
    if (a.source)      lines.push(`Source: ${a.source}`);
    if (a.assignedTo)  lines.push(`Assigned to: ${a.assignedTo}`);
    if (a.location)    lines.push(`Location: ${a.location}`);
    if (a.riskScore != null) lines.push(`Risk score: ${a.riskScore}`);
    if ((a.tags ?? []).length) lines.push(`Tags: ${a.tags!.join(', ')}`);
    return lines.join('\n');
  }
}
